import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { verifyRoute } from '@app/finxone-web-frontend/app/app.module';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { ProfileStateModel, UserInfoType } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import jwt_decode, { InvalidTokenError } from 'jwt-decode';
import { KeycloakService } from 'keycloak-angular';
import { filter, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { ClearAccountCache } from '../../actions/account.action';
import { ClearBeneficiaryCache } from '../../actions/beneficiary.action';
import { ClearNotificationsCache } from '../../actions/notification.action';
import { ClearTransactionsCache } from '../../actions/transactions.action';
import { CardManagementService } from '../../services/card-management-service/card-management-service';
import { ConfigService } from '../../services/config-service/config-service.service';
import { DeviceEnrollmentService } from '../../services/device-enrollment-service/device-enrollment.service';
import { DeviceTypeService } from '../../services/device-type/device-type.service';
import { FormActionState } from '../../state/form-submision.state';
import { ProjectSettingsState } from '../../state/project-settings.state';
import { ProfileState } from '../../state/user-profile.state';

@Component({ standalone: false, selector: 'app-post-auth', templateUrl: './post-auth.component.html' })
export class PostAuthComponent {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  constructor(
    protected keycloakService: KeycloakService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private configService: ConfigService,
    private cardService: CardManagementService,
    private deviceEnrollmentService: DeviceEnrollmentService,
    public deviceTypeService: DeviceTypeService,
  ) {
    // last-resort recovery: if still sitting on post-auth after 8 seconds the
    // profile fetch or routing has wedged - hard reload, preserving the deep link
    setTimeout(() => {
      if (window.location.pathname.includes('post-auth')) {
        console.log('/post-auth 8 second timeout hit, reloading...');
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        window.location.href = returnUrl?.startsWith('/') ? returnUrl : '/';
      }
    }, 8000);
    const redirectToOrgOnboardingFlow =
      this.store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData
        ?.redirectToOrgOnboardingFlow ?? false;
    if (redirectToOrgOnboardingFlow) {
      this.router.navigateByUrl('/zones/organisation-onboarding/onboarding/organisation-anonymous/default');
      return;
    }
    const previousUserId = localStorage.getItem('lastLoggedInId');
    const token = sessionStorage.getItem('token') as string;
    const windowToken = (window as any).accessToken;

    console.log('/post-auth window token exists:', !!windowToken);
    console.log('/post-auth sessionStorage token exists:', !!token);
    console.log('/post-auth previousUserId:', previousUserId);

    const activeToken = token || windowToken;

    try {
      console.log('/post-auth attempting to decode token...');
      const jwt: any = jwt_decode(activeToken);
      const newUserId = jwt.sub;
      console.log('/post-auth newUserId:', newUserId);

      if (previousUserId && previousUserId !== newUserId) {
        localStorage.removeItem('account');
        localStorage.removeItem('beneficiary');
        localStorage.removeItem('transactions');
        localStorage.removeItem('profile');
        localStorage.removeItem('notifications');
        store.dispatch(new ClearAccountCache());
        store.dispatch(new ClearBeneficiaryCache());
        store.dispatch(new ClearTransactionsCache());
        store.dispatch(new ClearNotificationsCache());
      }
      const redirectForApplePayEnabled = configService.config.features?.redirectForApplePayEnabled ?? true;
      const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? undefined;
      const kycStatusRoutingConfig =
        this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig
          ?.kycStatusRoutingConfig ?? null;
      console.log(
        `/post-auth waiting on profile state with redirectForApplePayEnabled: ${redirectForApplePayEnabled} and returnUrl: ${returnUrl}`,
      );

      // GetProfile is dispatched during app init (InitializationService) - wait
      // for it to land in state rather than re-fetching the user here
      this.store
        .select(ProfileState.getProfile)
        .pipe(
          filter((profile) => !!profile?.id),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe((profile) => {
          performance.mark('boot:post-auth-navigate');
          verifyRoute(
            router,
            profile as UserInfoType & ProfileStateModel,
            true,
            cardService,
            store,
            newUserId,
            redirectForApplePayEnabled,
            returnUrl,
            deviceTypeService,
            kycStatusRoutingConfig,
            route,
          );
        });

      const deviceEnrollmentEnabled = configService.config.features?.deviceEnrollmentEnabled ?? false;
      const isMobileApp = localStorage.getItem('APP_PREFERENCES') !== null;
      if (deviceEnrollmentEnabled && !isMobileApp) {
        this.handleDeviceEnrollment(activeToken);
      }
    } catch (err) {
      console.log('/post-auth error block:', err);
      // token decode issue again send to root of app
      // more graceful recovery likely needed
      // InvalidTokenError is expected here (email link-prefetch bots, expired
      // sessions landing on /post-auth with no token) - don't report it
      if (!(err instanceof InvalidTokenError)) {
        Sentry.captureException(err);
      }
      console.log('/post-auth navigating to root');
      router.navigateByUrl('');
    }
  }

  private async handleDeviceEnrollment(accessToken: string): Promise<void> {
    try {
      const deviceInfo = this.deviceEnrollmentService.getDeviceInfo();

      const deviceInfoRequest = {
        publicKey: 'NOT_USED',
        deviceId: deviceInfo.deviceId,
        deviceInfo: deviceInfo.deviceModel,
        alias: deviceInfo.deviceModel,
      };

      const devices = await firstValueFrom(this.deviceEnrollmentService.getEnrolledDevices(accessToken));

      if (devices) {
        if (devices.credentials && devices.credentials.length > 0) {
          if (devices.credentials[0].deviceId !== deviceInfo.deviceId) {
            const replaceEnrolmentRequest = {
              publicKey: 'NOT_USED',
              deviceId: deviceInfo.deviceId,
              deviceInfo: deviceInfo.deviceModel,
              alias: deviceInfo.deviceModel,
              oldAlias: devices.credentials[0].alias,
            };

            await firstValueFrom(
              this.deviceEnrollmentService.replaceEnrolledDevice(accessToken, replaceEnrolmentRequest),
            );
            console.log('/post-auth device enrollment replaced');
          }
        } else {
          await firstValueFrom(this.deviceEnrollmentService.enrollDevice(accessToken, deviceInfoRequest));
          console.log('/post-auth device enrolled');
        }
      }
    } catch (error) {
      console.error('/post-auth device enrollment error:', error);
      Sentry.captureException(error);
    }
  }
}
